/**
 * Port of HALO's `getHaloPhotos` util, adapted to the photo field shapes
 * the current Rentals API returns.
 *
 * Listing detail payloads expose photos through several historically-layered
 * fields:
 *   - `largePhotos[]`     flat `{url, caption}[]` (legacy HALO shape)
 *   - `listingPhotos[]`   `{thumb, squareThumb, medium, large}[]` (current)
 *   - `photos[]`          older flat array
 *   - `medPhotoUrls[]`    fallback: array of medium URL strings
 *   - `photoUrl`          single URL string
 *   - `previewPhoto`      `{url, caption}` preview
 *
 * We walk them in order of preference, append unique floorplan photos, and
 * fall back to a no-photo sentinel only when nothing is available.
 */

export type HaloPhoto = {
  url: string;
  caption?: string;
  noPhoto?: boolean;
};

export const NO_PHOTO_ALT_TEXT = 'This listing has no photos';

type RawSizedPhoto = {
  thumb?: { url?: string };
  squareThumb?: { url?: string };
  medium?: { url?: string };
  large?: { url?: string };
  caption?: string;
};

type FloorPlan = {
  largePhotos?: Array<HaloPhoto>;
  listingPhotos?: Array<RawSizedPhoto>;
  modelId?: string;
};

type ListingLike = {
  largePhotos?: Array<HaloPhoto>;
  photos?: Array<HaloPhoto>;
  listingPhotos?: Array<RawSizedPhoto>;
  medPhotoUrls?: Array<string>;
  photoUrl?: string;
  floorplans?: Array<FloorPlan>;
  previewPhoto?: HaloPhoto;
};

const NO_PHOTO_PLACEHOLDER: HaloPhoto = {
  url: '',
  caption: NO_PHOTO_ALT_TEXT,
  noPhoto: true,
};

const noImageFound = (): Array<HaloPhoto> => [NO_PHOTO_PLACEHOLDER];

/** Pick the biggest size variant available from a listingPhotos entry. */
function flattenSizedPhoto(raw: RawSizedPhoto | undefined): HaloPhoto | null {
  if (!raw) return null;
  const url = raw.large?.url ?? raw.medium?.url ?? raw.thumb?.url ?? raw.squareThumb?.url;
  if (!url) return null;
  return { url, caption: raw.caption };
}

function getFloorplanPhotos(listing: ListingLike): Array<HaloPhoto> {
  const out: Array<HaloPhoto> = [];
  for (const fp of listing.floorplans ?? []) {
    for (const photo of fp.largePhotos ?? []) {
      if (photo?.url) out.push(photo);
    }
    for (const raw of fp.listingPhotos ?? []) {
      const p = flattenSizedPhoto(raw);
      if (p) out.push(p);
    }
  }
  return out;
}

export function getHaloPhotos(listing: ListingLike | null | undefined): Array<HaloPhoto> {
  if (!listing) return noImageFound();

  let photos: Array<HaloPhoto> = [];

  if (listing.largePhotos?.length) {
    photos = photos.concat(listing.largePhotos.filter((p): p is HaloPhoto => Boolean(p?.url)));
  } else if (listing.listingPhotos?.length) {
    photos = photos.concat(
      listing.listingPhotos.map(flattenSizedPhoto).filter((p): p is HaloPhoto => p !== null),
    );
  } else if (listing.photos?.length) {
    photos = photos.concat(listing.photos.filter((p): p is HaloPhoto => Boolean(p?.url)));
  } else if (listing.medPhotoUrls?.length) {
    // Upgrade `medium` → `large` URLs where the Zillow CDN supports it.
    photos = photos.concat(
      listing.medPhotoUrls
        .filter((url) => typeof url === 'string' && url.length > 0)
        .map((url) => ({
          url: url.replace('_medium_500_500', '_large_1200_1200').replace(/medium/, 'large'),
        })),
    );
  }

  const floorplanPhotos = getFloorplanPhotos(listing);
  const seenUrls = new Set(photos.map((p) => p.url));
  for (const photo of floorplanPhotos) {
    if (!seenUrls.has(photo.url)) {
      seenUrls.add(photo.url);
      photos.push(photo);
    }
  }

  if (photos[0]?.url) return photos;

  if (listing.previewPhoto?.url && !listing.previewPhoto.noPhoto) {
    const previewPhoto = { ...listing.previewPhoto };
    if (previewPhoto.url.includes('thumb')) {
      previewPhoto.url = previewPhoto.url.replace(/thumb/, 'large');
    } else if (previewPhoto.url.includes('medium')) {
      previewPhoto.url = previewPhoto.url.replace(/medium/, 'large').replaceAll('500', '1200');
    }
    return [previewPhoto];
  }

  if (listing.photoUrl) {
    return [
      {
        url: listing.photoUrl
          .replace('_squarethumb_74_74', '_large_1200_1200')
          .replace('_thumb_180_180', '_large_1200_1200')
          .replace(/thumb/, 'large')
          .replace(/medium/, 'large'),
      },
    ];
  }

  return noImageFound();
}
